/**
 * MealPlanSection — the "AI Meal Plan" surface embedded in NutritionPage.
 *
 * Owns:
 *   - Current active plan (GET /api/meal-plan)
 *   - Persisted preferences (GET/PUT /api/nutrition/preferences)
 *   - Generation (POST /api/meal-plan/generate) with per-generation overrides
 *   - One-tap log to today's food log (POST /api/meal-plan/log-meal)
 *   - Shopping-list drawer (data comes with the plan)
 */
import React, { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { toast } from "sonner";
import { Sparkles, Loader2, Sliders, ShoppingBag, Plus, ChefHat } from "lucide-react";
import MealPlanPreferences from "./MealPlanPreferences";
import ShoppingListDrawer from "./ShoppingListDrawer";

const SLOT_LABEL = { breakfast: "Breakfast", lunch: "Lunch", snack: "Snack", dinner: "Dinner" };

export default function MealPlanSection({ onFoodLogged }) {
  const [plan, setPlan] = useState(null);
  const [prefs, setPrefs] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [generateElapsed, setGenerateElapsed] = useState(0);
  const [prefsOpen, setPrefsOpen] = useState(false);
  const [shoppingOpen, setShoppingOpen] = useState(false);
  const [selectedDay, setSelectedDay] = useState(0);
  const [expandedMeal, setExpandedMeal] = useState(null); // `${dayIdx}-${slot}`

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const [p, pr] = await Promise.all([
          api.get("/meal-plan"),
          api.get("/nutrition/preferences"),
        ]);
        if (!alive) return;
        setPlan(p.data.plan === null ? null : p.data);
        setPrefs(pr.data);
      } catch (err) {
        // Non-fatal — user might not be logged in yet or endpoints unreachable.
        console.warn("Meal plan load failed", err);
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, []);

  const savePrefs = async (nextPrefs) => {
    try {
      const { data } = await api.put("/nutrition/preferences", nextPrefs);
      setPrefs(data);
      setPrefsOpen(false);
      toast.success("Preferences saved");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not save preferences");
    }
  };

  const generatePlan = async (overridePrefs) => {
    setGenerating(true);
    setGenerateElapsed(0);
    try {
      // Persist prefs first so future generations use the same defaults.
      await api.put("/nutrition/preferences", overridePrefs);
      setPrefs(overridePrefs);

      // Kick off the async job. This POST returns quickly (job_id + status).
      const { data: jobStart } = await api.post("/meal-plan/generate", overridePrefs);
      setPrefsOpen(false);
      const jobId = jobStart.job_id;

      // Poll every 3s up to ~3 min. Claude typically takes 60-90s for the
      // 7-day plan; the polling cap gives us headroom for slow LLM responses.
      const started = Date.now();
      const MAX_WAIT_MS = 3 * 60 * 1000;
      const POLL_INTERVAL_MS = 3000;

      while (true) {
        setGenerateElapsed(Math.round((Date.now() - started) / 1000));
        await new Promise((r) => setTimeout(r, POLL_INTERVAL_MS));

        const { data: status } = await api.get(`/meal-plan/generate/${jobId}`);
        if (status.status === "done" && status.plan) {
          setPlan(status.plan);
          setSelectedDay(0);
          toast.success("Fresh 7-day plan ready 🎉");
          break;
        }
        if (status.status === "failed") {
          throw new Error(status.error || "AI meal plan generation failed");
        }
        if (Date.now() - started > MAX_WAIT_MS) {
          throw new Error("Meal plan generation timed out — try again in a minute.");
        }
      }
    } catch (err) {
      const detail = err.response?.data?.detail || err.message;
      if (err.response?.status === 402) {
        toast.error("Pro required for AI meal planning — check /billing");
      } else {
        toast.error(detail || "Could not generate plan");
      }
    } finally {
      setGenerating(false);
      setGenerateElapsed(0);
    }
  };

  const logMeal = async (dayIndex, mealSlot) => {
    try {
      await api.post("/meal-plan/log-meal", { day_index: dayIndex, meal_slot: mealSlot });
      toast.success("Logged to today's food");
      onFoodLogged?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not log meal");
    }
  };

  // ---- Empty state ----
  if (loading) {
    return (
      <div className="border border-white/10 bg-[#121212] p-6 inline-flex items-center gap-2 text-white/40 text-sm">
        <Loader2 className="w-4 h-4 animate-spin" /> Loading meal plan…
      </div>
    );
  }

  return (
    <div className="border border-white/10 bg-[#121212]" data-testid="meal-plan-section">
      {/* Header */}
      <div className="p-6 border-b border-white/10 flex items-start justify-between gap-4">
        <div>
          <div className="label-eyebrow text-[#6B9BFF] inline-flex items-center gap-1.5 mb-1">
            <ChefHat className="w-3 h-3" /> AI Meal Plan
          </div>
          <h3 className="font-display text-2xl leading-tight">
            {plan ? "This week's meals" : "Personalised 7-day meal plans"}
          </h3>
          {plan?.summary && (
            <p className="text-xs text-white/50 mt-1">{plan.summary}</p>
          )}
          {plan?.targets && (
            <p className="text-[11px] text-white/40 mt-1 tabular-nums" data-testid="meal-plan-targets">
              Targeting <span className="text-white/70 font-bold">{plan.targets.calories} kcal</span>/day ·
              {" "}{plan.targets.protein_g}P · {plan.targets.carbs_g}C · {plan.targets.fat_g}F
            </p>
          )}
        </div>
        <div className="flex flex-col sm:flex-row gap-2 shrink-0">
          <button
            onClick={() => setPrefsOpen(true)}
            className="text-white/70 hover:text-white border border-white/20 hover:border-white/40 text-[10px] uppercase tracking-widest font-bold px-3 py-2 inline-flex items-center gap-1.5"
            data-testid="meal-plan-open-prefs"
          >
            <Sliders className="w-3 h-3" /> {plan ? "Preferences" : "Set up"}
          </button>
          {plan && (
            <button
              onClick={() => setShoppingOpen(true)}
              className="text-white/70 hover:text-white border border-white/20 hover:border-white/40 text-[10px] uppercase tracking-widest font-bold px-3 py-2 inline-flex items-center gap-1.5"
              data-testid="meal-plan-open-shopping"
            >
              <ShoppingBag className="w-3 h-3" /> Shopping list
            </button>
          )}
        </div>
      </div>

      {/* Body */}
      {!plan ? (
        <div className="p-6 text-center" data-testid="meal-plan-empty">
          <p className="text-sm text-white/60 mb-4 max-w-md mx-auto">
            Generate a full week of meals that hit your calorie target, respect your dietary
            preferences, and come with a ready-to-shop grocery list.
          </p>
          <button
            onClick={() => setPrefsOpen(true)}
            disabled={generating}
            className="bg-[#6B9BFF] hover:bg-white text-black font-black uppercase tracking-widest text-xs px-5 py-3 inline-flex items-center justify-center gap-2 disabled:opacity-50"
            data-testid="meal-plan-generate-cta"
          >
            {generating ? (
              <><Loader2 className="w-4 h-4 animate-spin" /> Generating… {generateElapsed}s</>
            ) : (
              <><Sparkles className="w-4 h-4" /> Generate 7-day plan</>
            )}
          </button>
          {generating && (
            <p className="text-[11px] text-white/40 mt-3" data-testid="meal-plan-generating-hint">
              This usually takes 60-90 seconds — feel free to switch tabs.
            </p>
          )}
        </div>
      ) : (
        <div>
          {/* Day tabs */}
          <div className="border-b border-white/10 flex items-center gap-0.5 overflow-x-auto" data-testid="meal-plan-day-tabs">
            {(plan.days || []).map((day, i) => (
              <button
                key={day.day_name || i}
                onClick={() => setSelectedDay(i)}
                className={`text-[11px] uppercase tracking-widest font-bold px-4 py-3 whitespace-nowrap transition-colors border-b-2 ${
                  i === selectedDay
                    ? "text-[#6B9BFF] border-[#6B9BFF]"
                    : "text-white/50 border-transparent hover:text-white"
                }`}
                data-testid={`meal-plan-day-${i}`}
              >
                {(day.day_name || `Day ${i + 1}`).slice(0, 3)}
              </button>
            ))}
          </div>

          {/* Meals for the selected day */}
          <div className="divide-y divide-white/5" data-testid={`meal-plan-day-${selectedDay}-meals`}>
            {(plan.days?.[selectedDay]?.meals || []).map((meal) => {
              const key = `${selectedDay}-${meal.slot}`;
              const expanded = expandedMeal === key;
              return (
                <div key={key} className="p-5" data-testid={`meal-card-${meal.slot}`}>
                  <div className="flex items-start justify-between gap-3 mb-2">
                    <div className="flex-1 min-w-0">
                      <div className="label-eyebrow text-white/40 text-[10px] mb-1">
                        {SLOT_LABEL[meal.slot] || meal.slot}
                        {meal.prep_time_min ? ` · ${meal.prep_time_min} min` : ""}
                      </div>
                      <div className="font-bold text-white truncate">{meal.name}</div>
                      <div className="text-xs text-white/50 mt-1">
                        {Math.round(meal.protein_g || 0)}P · {Math.round(meal.carbs_g || 0)}C · {Math.round(meal.fat_g || 0)}F
                      </div>
                    </div>
                    <div className="font-display text-2xl text-[#6B9BFF] shrink-0">
                      {Math.round(meal.calories || 0)}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 mt-2">
                    <button
                      onClick={() => setExpandedMeal(expanded ? null : key)}
                      className="text-[10px] uppercase tracking-widest font-bold text-white/60 hover:text-white border border-white/20 hover:border-white/40 px-3 py-2"
                      data-testid={`meal-card-${meal.slot}-recipe-toggle`}
                    >
                      {expanded ? "Hide recipe" : "Recipe"}
                    </button>
                    <button
                      onClick={() => logMeal(selectedDay, meal.slot)}
                      className="text-[10px] uppercase tracking-widest font-bold text-black bg-[#6B9BFF] hover:bg-white px-3 py-2 inline-flex items-center gap-1"
                      data-testid={`meal-card-${meal.slot}-log`}
                    >
                      <Plus className="w-3 h-3" /> Log this meal
                    </button>
                  </div>
                  {expanded && (
                    <div className="mt-3 pt-3 border-t border-white/10 grid sm:grid-cols-2 gap-4 text-xs">
                      <div>
                        <div className="label-eyebrow text-white/50 text-[10px] mb-1.5">Ingredients</div>
                        <ul className="space-y-1 text-white/70">
                          {(meal.ingredients || []).map((ing, i) => (
                            <li key={i} className="flex gap-2"><span className="text-white/30">•</span>{ing}</li>
                          ))}
                        </ul>
                      </div>
                      <div>
                        <div className="label-eyebrow text-white/50 text-[10px] mb-1.5">Steps</div>
                        <ol className="space-y-1 text-white/70">
                          {(meal.steps || []).map((step, i) => (
                            <li key={i} className="flex gap-2"><span className="text-white/30">{i + 1}.</span>{step}</li>
                          ))}
                        </ol>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          <div className="p-5 border-t border-white/10 text-center">
            <button
              onClick={() => setPrefsOpen(true)}
              disabled={generating}
              className="text-[10px] uppercase tracking-widest font-bold text-white/60 hover:text-white disabled:opacity-50"
              data-testid="meal-plan-regenerate"
            >
              {generating ? `Generating… ${generateElapsed}s` : "↻ Regenerate a new week"}
            </button>
          </div>
        </div>
      )}

      <MealPlanPreferences
        open={prefsOpen}
        initial={prefs}
        onClose={() => setPrefsOpen(false)}
        onSave={savePrefs}
        onGenerate={generatePlan}
        busy={generating}
        elapsedSec={generateElapsed}
      />

      <ShoppingListDrawer
        open={shoppingOpen}
        shoppingList={plan?.shopping_list || []}
        onClose={() => setShoppingOpen(false)}
      />
    </div>
  );
}
